import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  styled,
} from '@mui/material';
import { Close, Search, Mic, Send, AttachFile } from '@mui/icons-material';
import { TemplateMessenger } from '../TemplateMessenger';
import { useWhatsappClient } from '../WhatsappClientContext';
import { ChatContainer } from '../ChatContainer';
import { SessionCountdown } from '../SessionCountdown';
import { useNavigate } from 'react-router-dom';
import { routes } from '@frontend/router/routes';
import { useGetAllCases } from '@frontend/features/cases/queries';
import { addLeadingZeros, escapeFilename } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { ChipCustom } from '@frontend/features/common/components/ChipCustom';
import { useTranslation } from 'react-i18next';
import { usePasteHandler } from '../../hooks/usePasteHandler';
import MessageInputWithEmoji from '../MessageInputWithEmoji';
import { ReplyMessageIndicator } from '../ReplyMessageIndicator/ReplyMessageIndicator';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface MessageInputRef {
  focus: () => void;
  blur: () => void;
  setSelectionRange: (start: number, end: number) => void;
}

const columnWidth = '396px';

// Input simple para mobile sin emoji integrado
const SimpleMessageInput = styled('input')(({ theme }) => ({
  ...theme.typography.subtitle2,
  border: 'none',
  outline: 'none',
  width: '100%',
  padding: theme.spacing(1),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  '&::placeholder': {
    color: theme.palette.text.disabled,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
  },
}));

// Esta esta para revisar
function stringToColor(string: string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      fontSize: '48px',
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}

const WhatsappClient = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const [openMessenger, setOpenMessenger] = useState(false);
  const [textBody, setTextBody] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [pendingMediaPreview, setPendingMediaPreview] = useState<{ file: File; url: string } | null>(null);
  const [pendingMediaKey, setPendingMediaKey] = useState<string | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const theme = useTheme();
  const isFullScreen = useMediaQuery('(max-width:1672px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<MessageInputRef>(null);
  const simpleInputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { handlePaste } = usePasteHandler({
    onFileSelect: (file) => {
      const uniqueName = `${Date.now()}_${file.name}`;
      const fileWithUniqueName = new File([file], uniqueName, { type: file.type });
      const fileUrl = URL.createObjectURL(fileWithUniqueName);
      setPendingMediaPreview({ file: fileWithUniqueName, url: fileUrl });
      setPendingMediaKey(uniqueName);
      setAudioUrl(null);
      setAudioChunks([]);
      setTextBody('');
    },
  });

  const handleGoToClientView = () => {
    if (clientData?.id) {
      navigate(routes.dashboard.clientView.replace(':id', clientData?.id));
    }
  };

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const {
    clientData,
    clientDataIsloading,
    messages,
    handleSendTextMessage,
    handleSendAudioMessage,
    handleSendMediaMessage,
    currentCaseId,
    isSessionExpired,
    isSending,
    messageToReply,
  } = useWhatsappClient();

  const { data: casesData, refetch: refetchCases } = useGetAllCases({
    pageSize: 10,
    currentPage: 1,
    clientIds: clientData?.id ? [clientData.id] : [],
    search: '',
  });

  useEffect(() => {
    if (open && clientData?.id) {
      refetchCases();
    }
  }, [open, clientData?.id, refetchCases]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !isSending && textBody.trim()) {
        handleSendTextMessage(textBody);
        setTextBody('');
      }
    },
    [textBody, isSending, handleSendTextMessage],
  );

  const handleSendMessage = () => {
    if (textBody.trim() && !isSending) {
      handleSendTextMessage(textBody);
      setTextBody('');
    }
  };

  const handleSendAudio = () => {
    if (audioUrl && !isSending) {
      try {
        handleSendAudioMessage(new Blob(audioChunks, { type: 'audio/wav' }), clientData?.id);
        setAudioUrl(null);
        setAudioChunks([]);
      } catch (error) {
        console.error('Error al enviar audio:', error);
      }
    }
  };

  const handleChange = useCallback((value: string) => {
    setTextBody(value);
  }, []);



  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        setAudioChunks(chunks);
        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const uniqueName = `${Date.now()}_${escapeFilename(file.name)}`;
      const fileWithUniqueName = new File([file], uniqueName, { type: file.type });
      const fileUrl = URL.createObjectURL(fileWithUniqueName);
      setPendingMediaPreview({ file: fileWithUniqueName, url: fileUrl });
      setPendingMediaKey(uniqueName);
      setAudioUrl(null);
      setAudioChunks([]);
      setTextBody('');
    }
  };

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSendFile = async () => {
    if (pendingMediaPreview && clientData?.id) {
      setIsUploadingMedia(true);
      await handleSendMediaMessage(
        clientData?.agencyId,
        pendingMediaPreview.file,
        clientData.id,
        pendingMediaPreview.file.name,
      );
    }
  };

  const handleCancelFileUpload = () => {
    setPendingMediaPreview(null);
    setPendingMediaKey(null);
    setIsUploadingMedia(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelAudioUpload = () => {
    setAudioUrl(null);
    setAudioChunks([]);
    setIsUploadingMedia(false);
  };

  useEffect(() => {
    if (pendingMediaKey && pendingMediaPreview && isUploadingMedia) {
      const found = messages.some(
        (msg) =>
          (msg.type === 'image' || msg.type === 'document' || msg.type === 'audio') &&
          !msg.inbound &&
          (msg.filename === pendingMediaKey || (typeof msg.text === 'string' && msg.text.includes(pendingMediaKey))),
      );

      if (found) {
        setPendingMediaKey(null);
        setPendingMediaPreview(null);
        setIsUploadingMedia(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  }, [messages, pendingMediaKey, pendingMediaPreview, isUploadingMedia]);

  // Loader: solo cuando se está enviando
  const showLoader = Boolean(isUploadingMedia && pendingMediaPreview && pendingMediaKey);

  // El mensaje se puede enviar si hay texto, audio o media pendiente (y no se está subiendo)
  const canSendMessage = !isUploadingMedia && (textBody.trim() || audioUrl || pendingMediaPreview);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Efecto para enfocar el input cuando se abre el drawer
  useEffect(() => {
    if (open) {
      timeoutRef.current = setTimeout(() => {
        if (!isSessionExpired) {
          if (isMobile && simpleInputRef.current) {
            simpleInputRef.current.focus();
          } else if (!isMobile && inputRef.current) {
            inputRef.current.focus();
          }
        }
      }, 300); // Espera a que Drawer termine de animar
    }

    // Cleanup function para limpiar el timeout
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open, isSessionExpired, isMobile]);

  const showReplyMessageIndicator = messageToReply && clientData?.id;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: '100vh',
          width: isFullScreen ? '100%' : '1672px',
          maxWidth: '100%',
          maxHeight: '100vh',
          borderTopLeftRadius: isFullScreen ? 0 : 2,
          borderBottomLeftRadius: isFullScreen ? 0 : 2,
        },
      }}
    >
      <Box
        display="flex"
        overflow="hidden"
        p={3}
        flexDirection="column"
        justifyContent="center"
        alignItems="stretch"
        gap={3}
        maxHeight="100vh"
        height="100%"
      >
        <Box display="flex" alignItems="center" gap={3}>
          {isMobile ? (
            // Mobile header: mostrar info del cliente
            <Box
              display="flex"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={2}>
                {clientDataIsloading ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Skeleton variant="text" width={150} height={24} />
                    <Skeleton variant="text" width={100} height={16} />
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column">
                    <Typography variant="h6">
                      {clientData?.firstName} {clientData?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {clientData?.whatsappNumber}
                    </Typography>
                  </Box>
                )}
              </Box>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
          ) : (
            // Desktop header: layout original
            <>
              <Box display="flex" width={columnWidth} gap={3} pr={4} alignItems="center">
                <Typography variant="h6">{t('whatsapp.client.conversation')}</Typography>
              </Box>
              <Box display="flex" flex={1}>
                <Box flex={1} display="flex" gap={3} justifyContent="space-between" alignItems="center">
                  <Box display="flex" flex={1} alignItems="center" gap={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('whatsapp.client.searchPlaceholder')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: theme.palette.text.disabled }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <IconButton onClick={onClose}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box display="flex" alignItems="stretch" gap={3} height={`calc(100vh - ${theme.spacing(12)})`}>
          {/* Columna lateral - solo mostrar en desktop */}
          {!isMobile && (
            <>
              {clientDataIsloading ? (
                <Skeleton variant="rectangular" height="100%" width={columnWidth} sx={{ borderRadius: 2 }} />
              ) : (
                <Box
                  p={2}
                  display="flex"
                  flexDirection="column"
                  width={columnWidth}
                  justifyContent="space-between"
                  border={`1px solid ${theme.palette.divider}`}
                  gap={3}
                  borderRadius={2}
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  <Box display="flex" flexDirection="column" gap={3} flex={1} overflow="hidden">
                    <Box
                      borderRadius={1}
                      p={2}
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      sx={{
                        backgroundColor: theme.palette.paper.background,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.grey[200],
                        },
                      }}
                      onClick={handleGoToClientView}
                    >
                      <Avatar
                        {...stringAvatar(`${clientData?.firstName} ${clientData?.lastName}`)}
                        sx={{ width: 100, height: 100, mb: 1 }}
                      />
                      <Typography variant="h6">
                        {clientData?.firstName} {clientData?.lastName}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {clientData?.whatsappNumber}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        backgroundColor: theme.palette.paper.background,
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      borderRadius={1}
                      p={2}
                      gap={2}
                    >
                      <Typography variant="subtitle2" color="text.primary">
                        {t('whatsapp.client.lastCases')}
                      </Typography>
                      <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <List sx={{ width: '100%' }}>
                          {casesData?.items.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('whatsapp.client.noCases')}
                            </Typography>
                          ) : (
                            casesData?.items.map((caseItem) => (
                              <ListItem
                                key={caseItem.id}
                                sx={{
                                  p: 0.2,
                                  cursor: 'pointer',
                                  backgroundColor:
                                    currentCaseId === caseItem.id ? theme.palette.grey[200] : 'transparent',
                                  border:
                                    currentCaseId === caseItem.id
                                      ? `1px solid ${theme.palette.grey[400]}`
                                      : '1px solid transparent',
                                  '&:hover': {
                                    backgroundColor: theme.palette.grey[100],
                                  },
                                  borderRadius: 1,
                                }}
                                onClick={() => navigate(routes.dashboard.caseView.replace(':id', String(caseItem.id)))}
                              >
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" color="text.secondary">
                                        #{addLeadingZeros(caseItem.id, defaultLeadingZeros)}
                                      </Typography>
                                      <ChipCustom
                                        size="small"
                                        label={t(`cases.status.${caseItem.status}`)}
                                        backgroundColor={theme.palette.cases[caseItem.status].bg}
                                        textColor={theme.palette.cases[caseItem.status].text}
                                        component="span"
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                      {caseItem.name}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    </Box>
                  </Box>

                  <Box>
                    <Button onClick={() => setOpenMessenger(true)}>{t('whatsapp.client.sendTemplate')}</Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {clientDataIsloading ? (
            <Skeleton variant="rectangular" height="100%" width="100%" sx={{ borderRadius: 2 }} />
          ) : (
            <Box flex={1} display="flex" flexDirection="column" gap={2} width="100%">
              <Box
                ref={containerRef}
                overflow="hidden"
                flex={1}
                border={`1px solid ${theme.palette.divider}`}
                borderRadius={2}
                sx={{
                  overflowY: 'auto',
                  backgroundColor: theme.palette.grey[100],
                }}
              >
                <ChatContainer casesData={casesData} currentCaseId={currentCaseId} />
              </Box>

              {isMobile ? (
                // Layout compacto para mobile
                <>
                  {/* Línea de botones y session countdown */}
                  <Box
                    display="flex"
                    justifyContent={showReplyMessageIndicator ? "space-between" : "flex-end"}
                    alignItems="center"
                    width="100%"
                    gap={1}
                  >
                    {/* SessionCountdown a la derecha */}
                    {showReplyMessageIndicator && (
                      <ReplyMessageIndicator messageToReply={messageToReply} clientId={clientData?.id} />
                    )}
                    <SessionCountdown />
                  </Box>

                  {/* Área de input */}
                  <Box
                    display="flex"
                    gap={1}
                    px={1}
                    height="62px"
                    borderRadius={2}
                    border={`1px solid ${theme.palette.divider}`}
                    alignItems="center"
                  >
                    {pendingMediaPreview ? (
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {pendingMediaPreview.file.type.startsWith('image/') ? (
                          <Box
                            component="img"
                            src={pendingMediaPreview.url}
                            sx={{
                              height: 40,
                              width: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                          />
                        ) : null}
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {pendingMediaPreview.file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(pendingMediaPreview.file.size / 1024).toFixed(1)} KB - {pendingMediaPreview.file.type}
                          </Typography>
                        </Box>
                        {showLoader && <CircularProgress size={28} />}
                        <IconButton size="small" onClick={handleCancelFileUpload} disabled={showLoader}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : audioUrl ? (
                      <>
                        <audio ref={audioRef} src={audioUrl} controls style={{ flex: 1 }} />
                        <IconButton size="small" onClick={handleCancelAudioUpload} disabled={showLoader}>
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {!isRecording && (
                          <SimpleMessageInput
                            ref={simpleInputRef}
                            disabled={isSessionExpired}
                            value={textBody}
                            onChange={(e) => setTextBody(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder=""
                            onPaste={(e) => handlePaste(e.nativeEvent)}
                          />
                        )}
                      </>
                    )}

                    {/* Botones principales de envío */}
                    <IconButton
                      onClick={audioUrl ? handleSendAudio : pendingMediaPreview ? handleSendFile : handleSendMessage}
                      disabled={isSessionExpired || !canSendMessage || isSending || showLoader}
                      color="primary"
                    >
                      <Send />
                    </IconButton>
                    <IconButton
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      disabled={!!pendingMediaPreview || isUploadingMedia || isSessionExpired}
                      sx={{
                        color: isRecording ? 'error.main' : 'primary.main',
                        '&:hover': {
                          backgroundColor: isRecording ? 'error.light' : 'primary.light',
                        },
                      }}
                    >
                      <Mic />
                    </IconButton>

                    <Box display="flex" alignItems="center" gap={0.5}>
                      <IconButton
                        onClick={handleAttachClick}
                        disabled={isRecording || !!audioUrl || !!pendingMediaPreview || isUploadingMedia || isSessionExpired}
                        color="primary"
                        size="small"
                      >
                        <AttachFile fontSize="small" />
                      </IconButton>
                    </Box>

                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                      accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    />
                  </Box>


                </>
              ) : (
                // Layout original para desktop
                <>
                  {showReplyMessageIndicator && (
                    <Box
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <ReplyMessageIndicator messageToReply={messageToReply} clientId={clientData?.id} />
                    </Box>)}
                  <Box
                    display="flex"
                    gap={1}
                    px={1}
                    height="62px"
                    borderRadius={2}
                    border={`1px solid ${theme.palette.divider}`}
                    alignItems="center"
                  >
                    {pendingMediaPreview ? (
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {pendingMediaPreview.file.type.startsWith('image/') ? (
                          <Box
                            component="img"
                            src={pendingMediaPreview.url}
                            sx={{
                              height: 40,
                              width: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                          />
                        ) : null}
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {pendingMediaPreview.file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(pendingMediaPreview.file.size / 1024).toFixed(1)} KB - {pendingMediaPreview.file.type}
                          </Typography>
                        </Box>
                        {showLoader && <CircularProgress size={28} />}
                        <IconButton size="small" onClick={handleCancelFileUpload} disabled={showLoader}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : audioUrl ? (
                      <>
                        <audio ref={audioRef} src={audioUrl} controls style={{ flex: 1 }} />
                        <IconButton size="small" onClick={handleCancelAudioUpload} disabled={showLoader}>
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {!isRecording && (
                          <MessageInputWithEmoji
                            ref={inputRef}
                            disabled={isSessionExpired}
                            value={textBody}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder={t('whatsapp.client.writeMessage')}
                            onEscapeKey={onClose}
                            onPaste={handlePaste}
                          />
                        )}
                      </>
                    )}
                    <IconButton
                      onClick={audioUrl ? handleSendAudio : pendingMediaPreview ? handleSendFile : handleSendMessage}
                      disabled={isSessionExpired || !canSendMessage || isSending || showLoader}
                      color="primary"
                    >
                      <Send />
                    </IconButton>
                    <IconButton
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      disabled={!!pendingMediaPreview || isUploadingMedia || isSessionExpired}
                      sx={{
                        color: isRecording ? 'error.main' : 'primary.main',
                        '&:hover': {
                          backgroundColor: isRecording ? 'error.light' : 'primary.light',
                        },
                      }}
                    >
                      <Mic />
                    </IconButton>
                    <IconButton
                      onClick={handleAttachClick}
                      disabled={isRecording || !!audioUrl || !!pendingMediaPreview || isUploadingMedia || isSessionExpired}
                      color="primary"
                    >
                      <AttachFile />
                    </IconButton>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                      accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    />
                    <SessionCountdown />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* TemplateMessenger modal - disponible para mobile y desktop */}
        <TemplateMessenger open={openMessenger} onClose={() => setOpenMessenger(false)} />
      </Box>
    </Drawer>
  );
};

export default WhatsappClient;
